import type { Question, SurveyResponse } from '../types';
import { AnswerPreview } from './AnswerPreview';

export class SurveyUIBuilder {
  static buildProgressSection(currentIndex: number, totalQuestions: number): string {
    const progress = ((currentIndex + 1) / totalQuestions) * 100;
    return `
      <div class="progress">
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${progress}%"></div>
        </div>
        <div class="progress-text">Question ${currentIndex + 1} of ${totalQuestions}</div>
      </div>
    `;
  }

  static buildHeaderSection(title: string, currentIndex: number): string {
    const backButton = currentIndex > 0
      ? '<button id="prev-btn" class="back-button">←</button>'
      : '<div class="back-button-placeholder"></div>';
    return `
      <div class="survey-header">
        ${backButton}
        <h1>${title}</h1>
        <div class="header-placeholder"></div>
      </div>
    `;
  }

  static buildQuestionSection(question: Question, questionHtml: string): string {
    const requiredIndicator = question.required ? '<span class="required-indicator">*</span>' : '';

    // For static/section questions, the questionHtml already contains the title
    // So we don't need to add the h2 header
    if (question.type === 'static' || question.type === 'section') {
      return `
        <div class="question-container">
          ${questionHtml}
        </div>
      `;
    }

    const descriptionHtml = question.description
      ? `<p style="white-space: pre-wrap; color: #64748b; font-size: 0.95rem; margin-top: 8px; margin-bottom: 16px;">${question.description}</p>`
      : '';

    return `
      <div class="question-container">
        <h2 style="white-space: pre-wrap;">${question.question}${requiredIndicator}</h2>
        ${descriptionHtml}
        ${questionHtml}
      </div>
    `;
  }

  static buildButtonSection(currentIndex: number, totalQuestions: number): string {
    const isLastQuestion = currentIndex >= totalQuestions - 1;
    const buttonText = isLastQuestion ? 'Submit' : 'Next';
    const buttonId = isLastQuestion ? 'submit-btn' : 'next-btn';
    return `<div class="buttons"><button id="${buttonId}">${buttonText}</button></div>`;
  }

  static buildSurveyContainer(sections: string[]): string {
    return `
      <div class="survey-container" style="background: transparent; box-shadow: none; border-radius: 0;">
        ${sections.join('')}
      </div>
    `;
  }

  static buildSuccessMessage(
    isAnonymous = false,
    referralCode?: string,
    surveyResponse?: SurveyResponse,
    questions?: Question[],
    payoutType?: 'billy' | 'mobile_wallet' | 'cash',
    payoutTypeMessage?: string,
    payoutTypeMessageTitle?: string
  ): string {
    const billyPlayStoreUrl = import.meta.env.VITE_BILLY_PLAYSTORE_URL;
    const billyDirectDownloadUrl = import.meta.env.VITE_BILLY_DIRECT_DOWNLOAD_URL;

    // Determine the reward section heading and message based on payout type (only used when referral code is provided)
    let rewardHeading = 'Reward Information';
    let rewardMessage = 'Your reward will be processed according to the survey terms.<br><strong style="color: #1e293b;">Check your account for updates.</strong>';

    if ((isAnonymous && referralCode) && (payoutType || payoutTypeMessage || payoutTypeMessageTitle)) {
      if (payoutType === 'billy') {
        rewardHeading = payoutTypeMessageTitle || 'Billy Reward';
        rewardMessage = payoutTypeMessage || 'Your reward will be sent to your Billy account.<br><strong style="color: #1e293b;">Keep participating for more opportunities!</strong>';
      } else if (payoutType === 'mobile_wallet') {
        rewardHeading = payoutTypeMessageTitle || 'Mobile Wallet Reward';
        rewardMessage = payoutTypeMessage || 'Your reward will be sent to your mobile wallet.<br><strong style="color: #1e293b;">Keep participating for more opportunities!</strong>';
      } else if (payoutType === 'cash') {
        rewardHeading = payoutTypeMessageTitle || 'Cash Reward';
        rewardMessage = payoutTypeMessage || 'Your cash reward will be processed shortly.<br><strong style="color: #1e293b;">Keep participating for more opportunities!</strong>';
      } else {
        // No payout type but has custom title or message
        if (payoutTypeMessageTitle) {
          rewardHeading = payoutTypeMessageTitle;
        }
        if (payoutTypeMessage) {
          rewardMessage = payoutTypeMessage;
        }
      }
    }

    const downloadButtons = billyDirectDownloadUrl
      ? `
        <a href="${billyPlayStoreUrl}" target="_blank" class="playstore-button" style="display: inline-block; text-decoration: none; transition: all 0.3s ease; margin-bottom: 12px;">
          <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" style="height: 80px; width: auto;" />
        </a>
        <p style="color: #94a3b8; font-size: 0.85rem; margin: 8px 0;">or</p>
        <a href="${billyDirectDownloadUrl}" target="_blank" class="direct-download-button" style="display: inline-block; background: #674fa3; color: white; padding: 10px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 0.9rem; transition: all 0.3s ease;">
          Download APK Directly
        </a>
      `
      : `
        <a href="${billyPlayStoreUrl}" target="_blank" class="playstore-button" style="display: inline-block; text-decoration: none; transition: all 0.3s ease;">
          <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" style="height: 80px; width: auto;" />
        </a>
      `;

    const rewardSection = (isAnonymous && referralCode) ? `
      <div class="reward-section" style="background: #ffffff; border: 2px solid #e2d9f3; border-radius: 16px; padding: 28px; margin-top: 12px; text-align: center; width: 100%; max-width: 400px; box-shadow: 0 4px 16px rgba(103, 79, 163, 0.08);">
        <h2 style="color: #674fa3; margin-bottom: 16px; font-size: 1.4rem; font-weight: 600;">${rewardHeading}</h2>
        <p style="color: #475569; margin-bottom: 16px; font-size: 0.95rem; line-height: 1.6;">${rewardMessage}</p>
        <div style="background: white; border: 2px solid #674fa3; border-radius: 12px; padding: 20px; margin: 20px 0; box-shadow: 0 2px 8px rgba(103, 79, 163, 0.1);">
          <p style="color: #64748b; font-size: 0.8rem; margin: 0 0 10px 0; font-weight: 500; text-transform: uppercase; letter-spacing: 1px;">Your Referral Code</p>
          <div style="color: #674fa3; font-size: 1.8rem; font-weight: 700; letter-spacing: 4px; font-family: 'Courier New', monospace;">${referralCode}</div>
        </div>
        ${downloadButtons}
      </div>
    ` : '';

    const answerPreview = (surveyResponse && questions) ? AnswerPreview.renderAnswerPreview(surveyResponse, questions) : '';

    return `
      <div class="survey-container success-container enhanced-success" style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; background: transparent; box-shadow: none; border-radius: 0; padding: 20px;">
        <img src="/billy_lock_check.png" alt="Success" width="140" height="140" style="margin-bottom: 16px;" />
        <h1 style="color: #22c55e; margin-bottom: 8px; font-size: 1.8rem;">Thank You!</h1>
        <p style="font-size: 1rem; color: #333; margin-bottom: 16px; text-align: center; line-height: 1.4;">Your survey has been submitted successfully.<br>We really appreciate your time and input.</p>
        ${rewardSection}
        ${answerPreview}
      </div>
      <style>
        .enhanced-success { animation: fadeInSuccess 0.7s cubic-bezier(.68,-0.55,.27,1.55); }
        @keyframes fadeInSuccess {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .playstore-button:hover { transform: scale(1.05); opacity: 0.9; }
        .playstore-button:active { transform: scale(0.98); }
        .direct-download-button:hover { background: #8b7bb8 !important; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(103, 79, 163, 0.3); }
        .direct-download-button:active { transform: translateY(0); }
      </style>
    `;
  }

  static buildDemoSuccessMessage(): string {
    return `
      <div class="survey-container success-container enhanced-success" style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; background: transparent; box-shadow: none; border-radius: 0;">
        <img src="/billy_lock_check.png" alt="Success" width="180" height="180" style="margin-bottom: 24px;" />
        <h1 style="color: #22c55e; margin-bottom: 12px;">Thank You!</h1>
        <p style="font-size: 1.2rem; color: #333; margin-bottom: 24px; text-align: center;">Your survey has been submitted successfully.<br><small>Note: This is a demo version</small></p>
      </div>
      <style>
        .enhanced-success { animation: fadeInSuccess 0.7s cubic-bezier(.68,-0.55,.27,1.55); }
        @keyframes fadeInSuccess {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      </style>
    `;
  }

  static buildSurveyInfoSection(name: string, description: string): string {
    return `
      <div class="survey-info">
        <h1 style="white-space: pre-wrap;">${name}</h1>
        <p style="white-space: pre-wrap;">${description}</p>
      </div>
    `;
  }

  static buildSurveyStatisticsSection(survey: any): string {
    if (!survey) return '';

    const currentResponses = survey.current_response_count || 0;
    const maxResponses = survey.max_responses_allowed;
    const surveyBy = survey.survey_by || 'Unknown';
    const responseText = `Current responses ${currentResponses}/${maxResponses || '∞'}`;

    return `
      <div class="survey-status" style="margin-bottom: 20px; text-align: center; padding: 16px 0;">
        <p style="margin: 0 0 8px 0; color: #64748b; font-size: 0.9rem;">${responseText}</p>
        <p style="margin: 0; color: #64748b; font-size: 0.9rem;">Survey By ${surveyBy}</p>
      </div>
    `;
  }
}
